import { Grid, LoadingOverlay, Modal, Space, TextInput } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { useServerGroupValidation } from '../hooks/useServerGroupValidation';
import { useUpsertGroupForm } from '../hooks/useUpsertGroupForm';
import type { GroupDto } from '../types/GroupDto';

interface NewGroupModalProps {
	close: () => void;
	isOpened: boolean;
	triggerListUpdate: () => void;
}

export function NewGroupModal({
	isOpened,
	close,
	triggerListUpdate,
}: NewGroupModalProps) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useUpsertGroupForm();

	const newGroupMutation = mutate<GroupDto>(
		`${organisation}/groups/add`,
		inputs,
	);

	useServerGroupValidation(newGroupMutation, form);

	const resetAndClose = () => {
		form.reset();
		close();
		successModal.close();
	};

	const updateAndClose = () => {
		resetAndClose();
		triggerListUpdate();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'New Group'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={newGroupMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={updateAndClose}
					title={'Group Added!'}
					body={'A new group has been added. Click Ok to continue...'}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newGroupMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<Grid>
						<Grid.Col>
							<TextInput
								label={'Group Name'}
								placeholder={'Insert name of new group...'}
								key={form.key('groupName')}
								{...form.getInputProps('groupName')}
							/>
						</Grid.Col>
					</Grid>
					<FormButtons close={resetAndClose} reducePaddingTop={false} />
				</form>
			</Modal>
		</>
	);
}
